// ===========================================
// Family Credit report (#642 Phase 2 Request 8)
// ===========================================
// Laura's spec: one list of every family with a credit balance (amount, date added, last
// movement, refund due), filtered by financial year or dates, with totals and a CSV. Credits never
// lapse; anything untouched for the review period (a setting, 12 months by default) is flagged
// for the owner to review, and listed first.
//
// OWNER-ONLY, like Retro Edits: the route (`OWNER_ONLY_SUBPATHS`), the nav item and the API all
// say so. The owner also sets the review period, the financial year's first month (shared with
// #374) and the terms line families see beside their credit, in the settings panel here.

import { useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Link,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Download as DownloadIcon, Settings as SettingsIcon } from '@mui/icons-material';
import { usePermissions } from '../../../hooks/usePermissions';
import { useOwnerTitle } from '../../../hooks/useOwnerTitle';
import {
  useExportFamilyCreditReport,
  useFamilyCreditReport,
  useFamilyCreditSettings,
  useFinancialYearSettings,
  useUpdateFamilyCreditSettings,
} from '../../../hooks/useFamilyCredit';
import type { FamilyCreditReportFilters } from '../../../api/familyCredit.api';
import { formatCurrencyFromDollars } from '../../../utils/currency';
import { apiErrorMessage } from '../../../utils/apiError';

/** A @db.Date key (YYYY-MM-DD) as dd/mm/yyyy, without a Date object. */
const dayKeyToAu = (key: string | null) => (key ? key.split('-').reverse().join('/') : '');
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const fyLabel = (fy: number, startMonth: number) =>
  startMonth === 1 ? String(fy) : `${fy}–${String((fy + 1) % 100).padStart(2, '0')}`;

export default function FamilyCreditReportPage() {
  const { isOwner } = usePermissions();
  const ownerTitle = useOwnerTitle();
  if (!isOwner) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Only your {ownerTitle} can open the Family Credit report.</Alert>
      </Box>
    );
  }
  return <FamilyCreditReport />;
}

function FamilyCreditReport() {
  const { schoolSlug } = useParams<{ schoolSlug: string }>();
  // No filter = the current financial year (the server's default; it names it in `period`).
  const [filters, setFilters] = useState<FamilyCreditReportFilters>({});
  const [mode, setMode] = useState<'fy' | 'dates'>('fy');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const { data, isLoading, isError, error } = useFamilyCreditReport(filters, true);
  const exportMutation = useExportFamilyCreditReport();

  const currentFy = data?.financialYear.current;
  const startMonth = data?.financialYear.startMonth ?? 7;
  const fyOptions = currentFy !== undefined ? Array.from({ length: 6 }, (_, i) => currentFy - i) : [];
  const selectedFy = filters.fy ?? currentFy ?? '';

  const money = (v: string) => formatCurrencyFromDollars(v);

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h4" gutterBottom>
            Family Credit
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Every family holding account credit, and what moved in the period. Credit never expires; anything
            untouched for {data?.reviewAfterMonths ?? 12} months is flagged for review.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            startIcon={<SettingsIcon />}
            onClick={() => setSettingsOpen((o) => !o)}
            aria-expanded={settingsOpen}
            aria-controls="family-credit-settings-panel"
          >
            Settings
          </Button>
          <Button
            variant="outlined"
            startIcon={exportMutation.isPending ? <CircularProgress size={16} /> : <DownloadIcon />}
            onClick={() => exportMutation.mutate(filters)}
            disabled={!data || data.families.length === 0 || exportMutation.isPending}
          >
            Export CSV
          </Button>
        </Stack>
      </Stack>

      <Collapse in={settingsOpen} unmountOnExit id="family-credit-settings-panel">
        <FamilyCreditSettingsPanel onClose={() => setSettingsOpen(false)} />
      </Collapse>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
            <TextField
              select
              size="small"
              label="Period"
              value={mode}
              onChange={(e) => {
                const next = e.target.value as 'fy' | 'dates';
                setMode(next);
                if (next === 'fy') setFilters(currentFy !== undefined ? { fy: currentFy } : {});
              }}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="fy">Financial year</MenuItem>
              <MenuItem value="dates">Dates</MenuItem>
            </TextField>
            {mode === 'fy' ? (
              <TextField
                select
                size="small"
                label="Financial year"
                value={selectedFy}
                onChange={(e) => setFilters({ fy: Number(e.target.value) })}
                sx={{ minWidth: 180 }}
              >
                {fyOptions.map((fy) => (
                  <MenuItem key={fy} value={fy}>
                    {fyLabel(fy, startMonth)}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <>
                <TextField
                  size="small"
                  type="date"
                  label="From"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  size="small"
                  type="date"
                  label="To"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <Button
                  variant="contained"
                  disabled={(!from && !to) || (!!from && !!to && from > to)}
                  onClick={() => setFilters({ ...(from ? { from } : {}), ...(to ? { to } : {}) })}
                >
                  Apply
                </Button>
              </>
            )}
            {data && (
              <Typography variant="body2" color="text.secondary">
                {data.period.label}
              </Typography>
            )}
          </Stack>
        </CardContent>
      </Card>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Alert severity="error">{apiErrorMessage(error, 'Could not load the family credit report.')}</Alert>
      ) : !data || data.families.length === 0 ? (
        <Alert severity="info">No family holds account credit, and none moved in this period.</Alert>
      ) : (
        <TableContainer component={Card}>
          {/* Below md the table scrolls sideways, so no cell wraps there (the family name, the headers). */}
          <Table
            size="small"
            sx={(theme) => ({ [theme.breakpoints.down('md')]: { '& .MuiTableCell-root': { whiteSpace: 'nowrap' } } })}
          >
            <TableHead>
              <TableRow>
                <TableCell>Family</TableCell>
                <TableCell align="right">Balance</TableCell>
                <TableCell align="right">Refund due</TableCell>
                <TableCell align="right">Available</TableCell>
                <TableCell>First added</TableCell>
                <TableCell>Last movement</TableCell>
                <TableCell align="right">Added</TableCell>
                <TableCell align="right">Applied</TableCell>
                <TableCell align="right">Refunded</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.families.map((f) => (
                <TableRow key={f.familyId} data-testid="family-credit-row">
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {f.parentId ? (
                        <Link
                          component={RouterLink}
                          to={`/${schoolSlug}/admin/families?parent=${encodeURIComponent(f.parentId)}`}
                        >
                          {f.familyName}
                        </Link>
                      ) : (
                        <span>{f.familyName}</span>
                      )}
                      {f.needsReview && <Chip size="small" color="warning" label="Review" />}
                    </Stack>
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{money(f.balance)}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{money(f.refundDue)}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{money(f.available)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{dayKeyToAu(f.firstAddedOn)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{dayKeyToAu(f.lastMovementOn)}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{money(f.addedInPeriod)}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{money(f.appliedInPeriod)}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{money(f.refundedInPeriod)}</TableCell>
                </TableRow>
              ))}
              <TableRow data-testid="family-credit-totals" sx={{ '& td': { fontWeight: 600 } }}>
                <TableCell>
                  Total ({data.families.length} {data.families.length === 1 ? 'family' : 'families'}
                  {data.totals.needsReview > 0 ? `, ${data.totals.needsReview} to review` : ''})
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{money(data.totals.balance)}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{money(data.totals.refundDue)}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{money(data.totals.available)}</TableCell>
                <TableCell />
                <TableCell />
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{money(data.totals.addedInPeriod)}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{money(data.totals.appliedInPeriod)}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{money(data.totals.refundedInPeriod)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

/** The owner's settings: the review period, the financial year's first month, the terms line. */
function FamilyCreditSettingsPanel({ onClose }: { onClose: () => void }) {
  const { data: credit, isError: creditError } = useFamilyCreditSettings(true);
  const { data: fy, isError: fyError } = useFinancialYearSettings(true);
  const save = useUpdateFamilyCreditSettings();
  const [months, setMonths] = useState<string | null>(null);
  const [startMonth, setStartMonth] = useState<number | null>(null);
  const [terms, setTerms] = useState<string | null>(null);

  if (creditError || fyError) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        Could not load the account credit settings. Close the panel and try again.
      </Alert>
    );
  }
  if (!credit || !fy) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }
  const monthsValue = months ?? String(credit.reviewAfterMonths);
  const startValue = startMonth ?? fy.startMonth;
  const termsValue = terms ?? credit.termsLine;
  const monthsNum = Number(monthsValue);
  const monthsValid = Number.isInteger(monthsNum) && monthsNum >= 1 && monthsNum <= 60;

  const submit = async () => {
    const patch: { reviewAfterMonths?: number; termsLine?: string } = {};
    if (monthsNum !== credit.reviewAfterMonths) patch.reviewAfterMonths = monthsNum;
    if (termsValue.trim() !== credit.termsLine) patch.termsLine = termsValue.trim();
    try {
      await save.mutateAsync({
        credit: patch,
        financialYear: startValue !== fy.startMonth ? { startMonth: startValue } : undefined,
      });
      onClose();
    } catch {
      // Shown below.
    }
  };

  return (
    <Card sx={{ mb: 3 }} data-testid="family-credit-settings">
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Account credit settings
        </Typography>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              size="small"
              type="number"
              label="Review credit untouched for (months)"
              value={monthsValue}
              onChange={(e) => setMonths(e.target.value)}
              error={!monthsValid}
              helperText={!monthsValid ? 'A whole number from 1 to 60' : ' '}
              inputProps={{ min: 1, max: 60 }}
              sx={{ minWidth: 260 }}
            />
            <TextField
              select
              size="small"
              label="Financial year starts in"
              value={startValue}
              onChange={(e) => setStartMonth(Number(e.target.value))}
              helperText="The report's financial years start on the 1st of this month"
              sx={{ minWidth: 220 }}
            >
              {MONTHS.map((m, i) => (
                <MenuItem key={m} value={i + 1}>
                  {m}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          <TextField
            size="small"
            fullWidth
            label="Line shown to families with their credit"
            value={termsValue}
            onChange={(e) => setTerms(e.target.value)}
            inputProps={{ maxLength: 300 }}
            helperText="On their invoices, the invoice PDF and their portal. Leave blank for none."
          />
          {save.isError && <Alert severity="error">{apiErrorMessage(save.error, 'Could not save the settings.')}</Alert>}
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="contained" onClick={submit} disabled={!monthsValid || save.isPending}>
              Save
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}
